import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, MessageCircle } from 'lucide-react';
import { FOOTER_LINKS, HOME_SECTIONS } from '../config/navigation';
import { SOCIAL_LINKS, STORE } from '../config/store';
import { homeShell, focusRing } from '../styles/ui';
import DecorLeaf from './DecorLeaf';

// Quick links: Contact and Privacy Policy from the app's own navigation model
// (so they can only point at routes that exist), then the social profiles —
// each shown only when deployment configuration provides a valid value.
const contactSection = HOME_SECTIONS.find((item) => item.hash === '#contact');
const INTERNAL_LINKS = [
  ...(contactSection ? [{ label: contactSection.label, to: `/${contactSection.hash}` }] : []),
  ...FOOTER_LINKS,
];
const SOCIAL = [
  SOCIAL_LINKS.instagram && { label: 'Instagram', href: SOCIAL_LINKS.instagram, Icon: Instagram },
  SOCIAL_LINKS.whatsapp && { label: 'WhatsApp', href: SOCIAL_LINKS.whatsapp, Icon: MessageCircle },
].filter(Boolean);

const quickLinkClass = `inline-flex items-center gap-1.5 text-sm text-emerald-100/90 hover:text-white transition-colors ${focusRing} focus-visible:ring-emerald-300 focus-visible:ring-offset-emerald-950`;

const Footer = () => {
  const [logoError, setLogoError] = React.useState(false);
  // Never a hardcoded year — the notice would silently go stale.
  const year = new Date().getFullYear();

  return (
    <footer className="relative overflow-hidden bg-gradient-to-b from-emerald-950 to-emerald-900 py-10 sm:py-12">
      <div className="pointer-events-none select-none absolute inset-0" aria-hidden="true">
        <DecorLeaf className="absolute -left-6 top-6 w-28 text-emerald-700/40 rotate-[24deg]" />
        <DecorLeaf className="absolute left-16 bottom-4 w-16 text-emerald-700/30 -rotate-[18deg]" />
        <DecorLeaf className="absolute -right-4 top-10 w-24 text-emerald-700/40 -rotate-[30deg]" />
        <DecorLeaf className="absolute right-24 bottom-6 w-14 text-emerald-700/30 rotate-[14deg]" />
        <span className="absolute -top-16 left-1/4 w-72 h-72 bg-emerald-700/20 rounded-full blur-3xl" />
      </div>

      <div className={`${homeShell} relative`}>
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.3fr_1fr] gap-8 lg:gap-10 items-start">
          {/* Brand */}
          <div className="text-center lg:text-left">
            {/* The official brand logo in its own colours: no filter and no
                recolouring. A small white backing gives its brown and green
                lettering contrast against the dark footer. */}
            {!logoError ? (
              <span className="inline-flex bg-white rounded-[12px] border border-white/60 px-3 py-1.5 sm:px-4 sm:py-2">
                <img
                  src="/fresh-homely-logo.png"
                  alt={STORE.name}
                  onError={() => setLogoError(true)}
                  className="block h-[72px] sm:h-[84px] w-auto object-contain"
                />
              </span>
            ) : (
              <p className="serif text-3xl text-white">{STORE.name}</p>
            )}
            <p className="text-emerald-100/80 text-sm mt-3">Good Food. Happier Homes.</p>
            <p className="text-emerald-300/60 text-xs mt-5">
              &copy; {year} {STORE.name}. All rights reserved.
            </p>
          </div>

          {/* Brand statement */}
          <div className="text-center">
            <p className="serif italic text-white text-xl sm:text-2xl leading-snug">
              “From our store to your home, fresh every day.”
            </p>
            <DecorLeaf className="w-7 mx-auto mt-5 text-emerald-400/80" />
          </div>

          {/* Contact, privacy and the configured social profiles, then the
              published store contact details. No newsletter form exists. */}
          <div className="text-center lg:text-right">
            <h2 className="text-white font-bold text-sm uppercase tracking-[0.14em] mb-4">
              Quick Links
            </h2>
            <nav aria-label="Footer">
              {/* Two columns: Contact · Privacy Policy, then Instagram · WhatsApp. */}
              <ul className="inline-grid grid-cols-2 gap-x-8 gap-y-2.5 text-left">
                {INTERNAL_LINKS.map((item) => (
                  <li key={item.to}>
                    <Link to={item.to} className={quickLinkClass}>
                      {item.label}
                    </Link>
                  </li>
                ))}
                {SOCIAL.map(({ label, href, Icon }) => (
                  <li key={label}>
                    <a href={href} target="_blank" rel="noopener noreferrer" className={quickLinkClass}>
                      <Icon size={15} aria-hidden="true" />
                      {label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>

            <div className="mt-6 text-sm">
              <a
                href={STORE.phoneHref}
                className={`text-emerald-100/90 hover:text-white transition-colors ${focusRing} focus-visible:ring-emerald-300 focus-visible:ring-offset-emerald-950`}
              >
                {STORE.phone}
              </a>
              <address className="text-emerald-300/70 text-xs not-italic leading-relaxed mt-2 lg:ml-auto max-w-xs mx-auto lg:mr-0">
                {STORE.address}
              </address>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
